import * as Sentry from '@sentry/react';
import type { Event, EventHint } from '@sentry/react';

import { getClient, getEnv, detectWebGLSupport, isEmbedded } from 'utils/utils';
import beforeBreadcrumb from './breadcrumbs';

type BrowserSettingsContext = {
  documentLanguage?: string;
  preferredLanguage?: string;
  isTranslated?: boolean;
  isWebGlDisabled?: boolean;
};

const env: string = getEnv();
const isProduction: boolean = env === 'production';
const client: string = getClient();
const BROWSER_SETTINGS = 'browserSettings' as const;

const errorCodeIgnoreList: readonly string[] = ['null_origin', 'content_moderation_error'] as const;

function addTranslationDataToEventContext(event: Event): void {
  const documentLanguage: string = document.documentElement.lang;

  if (!event.contexts) {
    event.contexts = {};
  }
  if (!event.contexts[BROWSER_SETTINGS]) {
    event.contexts[BROWSER_SETTINGS] = {};
  }
  const browserSettings = event.contexts[BROWSER_SETTINGS] as BrowserSettingsContext;
  browserSettings.documentLanguage = documentLanguage;
  browserSettings.preferredLanguage = navigator.language;
  browserSettings.isTranslated = documentLanguage !== 'en-us'; // en-us is the value we set in index.html as the lang tag, changes to it would come from a third party translator
}

export function beforeSend(event: Event, hint: EventHint): Event | null {
  try {
    const { originalException } = hint;

    // Type guard for originalException
    if (
      originalException &&
      typeof originalException === 'object' &&
      'response' in originalException
    ) {
      const errorWithResponse = originalException as {
        response?: { data?: { code?: string } };
        sentryMessage?: string;
      };

      // filter out specific error codes
      const errorCode = errorWithResponse.response?.data?.code;
      if (errorCode && errorCodeIgnoreList.includes(errorCode)) return null;

      if (errorWithResponse.sentryMessage && event.exception?.values?.[0]) {
        // when errors have a dedicated sentryMessage property, send that rather than the default "message" property
        event.exception.values[0].value = errorWithResponse.sentryMessage;
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e: unknown) {
    // do nothing
  }

  // enrich with more context
  addTranslationDataToEventContext(event);

  if (env === 'local') {
    console.log('Sentry Event:', event);
    return null;
  }
  return event;
}

export default function initializeSentry(): void {
  Sentry.init({
    dsn: 'https://4acc2d0cb9ba4af586739550b525ea43@sentry.io/1875299',
    integrations: [
      // eslint-disable-next-line new-cap
      Sentry.browserTracingIntegration(),
      // https://docs.sentry.io/platforms/javascript/session-replay/
      Sentry.replayIntegration({
        maskAllText: false,
      }),
    ],
    release: process.env.REACT_APP_GIT_HASH,
    environment: `${env}`,
    tracesSampleRate: isProduction ? 1.0 : 0.1,
    replaysSessionSampleRate: 0, // we have a limit on session replays that should be reserved for errors only
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Load failed', // Azure maps failing to fetch
      'Analytics: Failed to fetch',
      'AxiosError: Request failed with status code 401',
    ],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    beforeBreadcrumb: beforeBreadcrumb as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    beforeSend: beforeSend as any,
  });
  Sentry.setTag('client', `${client}`);
  Sentry.setTag('mode', isEmbedded() ? 'embedded' : 'standalone');
  Sentry.setContext(BROWSER_SETTINGS, {
    isWebGlDisabled: !detectWebGLSupport(),
  });
}
